import { getFrontMessage } from 'appMessageDefinitions';

const debug = true;

import {
  AddressValues,
  PhotographerApplicationDTO,
  PhotographerCompanyInfos,
  SocialValues,
  UserInfosValues,
} from 'appAPITypes';
import * as yup from 'yup';
import { isDev } from 'appEnv';
import {
  EmptyMaterialPhotoSection,
  MaterialPhotoSectionValidation,
  MaterialPhotoSectionValues,
} from './formSections/materialPhoto';
import {
  EmptyMaterialVideoSection,
  MaterialVideoSectionValidation,
  MaterialVideoSectionValues,
} from './formSections/materialVideo';
import { Omit } from 'utility-types';
import { combineYupSchemas } from '../../utils/yupUtils';
import {
  EmptyMaterialMatterportSection,
  MaterialMatterportSectionValidation,
  MaterialMatterportSectionValues,
} from './formSections/materialMatterport';
import {
  EmptyMaterialDroneSection,
  MaterialDroneSectionValidation,
  MaterialDroneSectionValues,
} from './formSections/materialDrone';

import { useFrontCountry } from '../../layout/frontContext';
import {
  ShCountriesForPhotographer,
  ShPhotographerAvailabilities,
  ShPhotographerTransportations,
} from '@shoootin/config';

const EmptyUserInfosForm: UserInfosValues = {
  firstName: isDev && debug ? 'Lama' : '',
  lastName: isDev && debug ? 'Van Noisen' : '',
  email: isDev && debug ? 'lama@shooot.in' : '',
  mobilePhone: isDev && debug ? '+33 6 59 11 12 35' : '',
  skype: '',
};
const EmptyPhotographerCompanyInfosForm: PhotographerCompanyInfos = {
  companyId: isDev && debug ? '1234' : '',
  companyName: isDev && debug ? 'Shoootin' : '',
  companyType: isDev && debug ? 'SAS' : '',
};

const EmptySocialForm: SocialValues = {
  web: isDev && debug ? 'https://shoootin.com' : '',
  facebook: isDev && debug ? 'https://facebook.com' : '',
  behance: isDev && debug ? 'https://behance.com' : '',
  instagram: isDev && debug ? 'https://instagram.com' : '',
  linkedin: isDev && debug ? 'https://linkedin.com' : '',
  fivehundredpx: isDev && debug ? 'https://pageErrorpx.com' : '',
};

const EmptyAddressFormStatics: Omit<AddressValues, 'country'> = {
  address: '',
  latitude: undefined,
  longitude: undefined,
  city: '',
  cityId: undefined,
  zipcode: '',
};
const useEmptyAddressForm = (): AddressValues => {
  const country = useFrontCountry();
  return {
    ...EmptyAddressFormStatics,
    country,
  };
};

export const EmptyJoinFormStatics: Omit<
  PhotographerApplicationDTO,
  'address'
> = {
  id: '',
  ...EmptyMaterialPhotoSection,
  ...EmptyMaterialVideoSection,
  ...EmptyMaterialMatterportSection,
  ...EmptyMaterialDroneSection,
  userInfos: EmptyUserInfosForm,
  companyInfos: EmptyPhotographerCompanyInfosForm,
  social: EmptySocialForm,
  experiences: isDev && debug ? 'Jadore faire des photos' : '',
  about: isDev && debug ? 'Jadore faire des belles photos' : '',
  specialityList: isDev && debug ? ['wedding', 'portrait'] : [],
  availability: isDev && debug ? 'fullTime' : undefined,
  transportation: isDev && debug ? 'foot' : undefined,
  zones: [],
};

// Permit to initialize the country dropdown to the correct default country
export const useEmptyJoinForm = (): PhotographerApplicationDTO => {
  const address = useEmptyAddressForm();
  return {
    ...EmptyJoinFormStatics,
    address,
  };
};

const AddressFormValidation = yup.object().shape<AddressValues>({
  address: yup.string().trim(),
  country: yup.mixed().oneOf(ShCountriesForPhotographer as any),
  latitude: yup.number(),
  longitude: yup.number(),
  city: yup.string().trim(),
  cityId: yup.number(),
  zipcode: yup.string().trim(),
});

const UserInfosFormValidation = yup.object().shape<UserInfosValues>({
  firstName: yup
    .string()
    .trim()
    .required(getFrontMessage('form_userInfos_firstName_RequiredError').id),
  lastName: yup
    .string()
    .trim()
    .required(getFrontMessage('form_userInfos_lastName_RequiredError').id),
  email: yup
    .string()
    .trim()
    .email(getFrontMessage('form_userInfos_email_InvalidError').id)
    .required(getFrontMessage('form_userInfos_email_RequiredError').id),
  mobilePhone: yup
    .string()
    .trim()
    .required(getFrontMessage('form_userInfos_mobilePhone_RequiredError').id),
  skype: yup.string().trim().nullable(),
});

const SocialFormValidation = yup.object().shape<SocialValues>({
  behance: yup.string().trim(),
  facebook: yup.string().trim(),
  fivehundredpx: yup.string().trim(),
  instagram: yup.string().trim(),
  linkedin: yup.string().trim(),
  web: yup.string().trim(),
});

// Other means the remaining fields that are not "splitted"
// TOimprove we can split more and get more colocated validation
type OtherJoinFormValues = Omit<
  PhotographerApplicationDTO,
  | keyof MaterialPhotoSectionValues
  | keyof MaterialVideoSectionValues
  | keyof MaterialMatterportSectionValues
  | keyof MaterialDroneSectionValues
>;

const OtherJoinFormValidation = yup.object().shape<OtherJoinFormValues>({
  id: yup.string(),
  userInfos: UserInfosFormValidation,
  social: SocialFormValidation,
  experiences: yup.string().trim(),
  about: yup.string().trim(),
  specialityList: yup.array(),
  address: AddressFormValidation,
  availability: yup.mixed().oneOf(ShPhotographerAvailabilities as any),
  transportation: yup.mixed().oneOf(ShPhotographerTransportations as any),
  zones: yup.array(),
});

export const JoinFormValidation = combineYupSchemas<PhotographerApplicationDTO>(
  [
    MaterialPhotoSectionValidation,
    MaterialVideoSectionValidation,
    MaterialMatterportSectionValidation,
    MaterialDroneSectionValidation,
    OtherJoinFormValidation,
  ],
);
